import i18n from "@/lib/i18n";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportError } from "@/lib/error-reporting";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report the error
    reportError(error, {
      componentStack: errorInfo.componentStack,
      source: "error-boundary",
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    this.setState({ error, errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = import.meta.env.MODE === "development";

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{i18n.t("components.errorBoundary.somethingWentWrong")}</AlertTitle>
            <AlertDescription className="mt-2">
              {i18n.t("components.errorBoundary.weReSorryBut")}
              {isDevelopment && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">
                    {i18n.t("components.errorBoundary.technicalDetailsDev")}
                  </summary>
                  <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-32">
                    {this.state.error?.message}
                    {"\n\n"}
                    {this.state.error?.stack}
                  </pre>
                </details>
              )}
            </AlertDescription>
            <div className="mt-4 space-x-2">
              <Button onClick={this.handleReset} variant="outline">
                {i18n.t("components.errorBoundary.tryAgain")}
              </Button>
              <Button onClick={this.handleRefresh}>{i18n.t("components.errorBoundary.refreshPage")}</Button>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
